import _ from 'lodash-es';
import Link from '~/scripts/services/model/link';
import Node from '~/scripts/services/model/node';
import Relations from '~/scripts/services/relations';
import { ConfirmDeleteModal } from '~/utils/modals';
import APIV5 from '~/models/api';
import { COMPOSED_OF, DEFINED_BY } from '~/scripts/constants/common-kids';
import templateModalOoAdminLink from '../modal/modal-oo-admin-link.html';
import ModalOoAdminLinkController from '../modal/modal-oo-admin-link.controller';
import OoAdminController from '../oo-admin.controller';

/* globals Knitiv */

/**
 * @typedef {import('@knitiv/api-client-javascript').Definition} Definition
 */

/**
 * @typedef {Definition} RelationDefinition
 * @property {boolean} disabled
 */

export default class OoAdminRelationController {
	/* @ngInject */
	/**
	 *
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {Knitiv.Scope} $scope
	 * @param {import('angular').IQService} $q
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {import('~/scripts/services/model/node-service').NodeService} NodeService
	 * @param {import('~/scripts/services/model/repre').default} RepreService
	 */
	constructor(
		$injector,
		$scope,
		$q,
		$rootScope,
		$timeout,
		$uibModal,
		NodeService,
		RepreService,
	) {
		this.$injector = $injector;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$uibModal = $uibModal;
		this.NodeService = NodeService;
		this.RepreService = RepreService;
		this.$q = $q;
		this.$rootScope = $rootScope;

		/** @type {number} */
		this.currentPage = 0;

		/** @type {RelationDefinition[]} */
		this.kdef = [];

		/**
		 * @type {{
		 *   from: import('~/scripts/services/model/link').default[],
		 *   to: import('~/scripts/services/model/link').default[]
		 * }}
		 */
		this.links = {
			from: [],
			to:   [],
		};

		/** @type {?Node} */
		this.node = null;

		/** @type {number} */
		this.packetSize = 0;

		/** @type {?OoAdminController} */
		this.parent = null;

		/** @type {Node[]} */
		this.relations = [];

		/** @type {number} */
		this.totalItems = 0;
	}

	/**
	 * @param {MouseEvent} event
	 * @param {import('@knitiv/api-client-javascript').Definition & {disabled: boolean}} definition
	 */
	async addFrom(event, definition) {
		if (definition.disabled) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		const link = new Link({
			card_from:    1,
			card_to:      1,
			class_kid:    definition.objnum2_kid,
			flow:         'from',
			linker_kid:   definition.linker_kid,
			objnum_isa:   this.node?.isa.objid,
			objnum_kid:   this.node?.kid,
			relation_kid: definition.relation_kid,
		});

		const object = await this.$uibModal.open({
			template:     templateModalOoAdminLink,
			controller:   ModalOoAdminLinkController,
			controllerAs: '$ctrl',
			resolve:      {
				link:         () => link,
				edit:         () => false,
				isaDefinedBy: () => this.node?.isa_definedby,
			},
		}).result;

		if (object) {
			const newNode = new Node(this.$injector, object.link.objnum2_kid, object.link.isa);
			this.node?.addRelation(
				object.link.relation_kid,
				newNode,
				object.link.linker_kid,
				object.class_kid,
			);
			this.$timeout(() => {
				this.links.from.push(new Link({
					card_from:    object.link.card_from,
					card_to:      object.link.card_to,
					class_kid:    object.link.isa,
					flow:         'from',
					isa:          object.link.isa,
					isaname:      object.link.isaname,
					linker_kid:   definition.linker_kid,
					lock:         object.link.lock,
					objnum_isa:   this.node?.isa.objid,
					objnum_kid:   this.node?.kid,
					objnum2_isa:  object.link.objnum2_isa,
					objnum2_kid:  object.link.objnum2_kid,
					order:        object.link.order,
					relation_kid: definition.relation_kid,
				}));
				this.parent?.form?.$setDirty();
				this.setRelationsFromDef();
			});
		}
	}

	/**
	 * @param {any} link
	 */
	async delete(link) {
		const modal = new ConfirmDeleteModal(this.$injector);
		const answer = await modal.open(link.objname);

		if (answer) {
			this.node?.delRelation(link);
			_.remove(
				this.links.from,
				l => l.flow === link.flow
					&& l.relation_kid === link.relation_kid
					&& l.objnum2_kid === link.objnum2_kid
					&& l.linker_kid === link.linker_kid,
			);
			this.$timeout(() => {
				this.setRelationsFromDef();
				this.parent?.form?.$setDirty();
			});
		}
	}

	/**
	 * @param {import('~/scripts/services/model/link').default} link
	 */
	async edit(link) {
		/**
		 * @type {{link: import('~/scripts/services/model/link').default}}
		 */
		const object = await this.$uibModal.open({
			template:     templateModalOoAdminLink,
			controller:   ModalOoAdminLinkController,
			controllerAs: '$ctrl',
			resolve:      {
				link:         () => ({ ...link }),
				edit:         () => true,
				isaDefinedBy: () => this.node?.isa_definedby,
			},
		}).result;

		this.node?.modRelation(object.link);
		_.extend(link, object.link);
		this.parent?.form?.$setDirty();
	}

	/**
	 * @param {number} index
	 */
	fetchObject(index) {
		if (this.node?.kid) {
			this.$q
				.when(APIV5.instance.objectToMe({
					node_kid:     this.node.kid,
					packet_size:  this.packetSize,
					packet_index: index + 1,
					result_mode:  'LINK',
				}, { id: 'oo-admin-relation.fetch-object' }))
				.then(objects => {
					this.links.to.length = 0;
					for (const l of objects.links) {
						const link = new Link({
							...l,
							flow: 'to',
						});
						if (link.objnum_kid && link.objnum_kid in objects.dico) {
							link.objname = objects.dico[link.objnum_kid].name;
						}
						this.links.to.push(link);
					}

					if (this.totalItems === -1 && objects.nb !== undefined) {
						this.totalItems = objects.nb;
					}

					return objects;
				})
				.catch(error => {
					console.error(error);
				});
		}
	}

	getCurrentIndex() {
		return (this.currentPage - 1) * this.packetSize;
	}

	setRelationsFromDef() {
		this.kdef = Object.values(this.node?.kdef).map(definition => {
			if (definition.relation_kid === DEFINED_BY) {
				definition.relation_kid = COMPOSED_OF;
			}
			const maxItems = definition.card_to;
			return {
				...definition,
				disabled: this.links.from
					.filter(link => link.class_kid === definition.objnum2_kid
						&& link.relation_kid === definition.relation_kid
						&& link.linker_kid === definition.linker_kid,
					).length >= maxItems,
			};
		});
	}

	/**
	 * @param {'from' | 'to'} flow
	 * @returns {boolean}
	 */
	shouldShowPager(flow) {
		return this.links ? this.totalItems > this.links[flow].length : false;
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChange(changes) {
		if (changes.node) {
			this.setRelationsFromDef();
		}
	}

	$onInit() {
		this.totalItems = -1;
		this.currentPage = 1;
		this.packetSize = 20;

		Relations
			.get(this.$injector)
			.then(relations => {
				this.relations = relations.filter(
					relation => relation.kid && !this.NodeService.dynRelations().includes(relation.kid),
				) || [];
				return this.relations;
			})
			.catch(error => {
				console.error(error);
			});

		this.fetchObject(this.getCurrentIndex());

		this.links = {
			from: this.node?.link.filter(
				link => link.flow === 'from' && link.relation_kid
					&& !this.NodeService.dynRelations().includes(link.relation_kid),
			) || [],
			to:   [],
		};

		this.filterLinks = (/** @type {Link} */ link) => link.relation_kid
			&& !this.NodeService.dynRelations().includes(link.relation_kid);

		this.setRelationsFromDef();
	}
}
